import { useQuery } from '@tanstack/react-query';
import { ChevronDown, AlertCircleIcon } from 'lucide-react-native';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import { Keyboard } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlHelper,
  FormControlHelperText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectScrollView,
  SelectTrigger,
} from '@/components/ui/select';
import { useColors } from '@/hooks/useColors';
import { InquiryDraftFormData } from '@/hooks/useInquiryForm';
import { getCancerTypesV2QueryOptions } from '@/services/getCancerTypesV2';

interface CancerTypeSelectProps {
  control: Control<InquiryDraftFormData>;
  errors: FieldErrors<InquiryDraftFormData>;
  isDisabled: boolean;
  expertCancerFocuses?: { id: number }[];
  onCancerTypeChange?: (val: string) => void;
  /**
   * A cancer type named elsewhere that matched none of the options, such as an
   * Ask conversation's. Shown under the field while nothing is selected.
   */
  hint?: string;
}

/**
 * Cancer Type Selection Component
 *
 * Dropdown selector for cancer types that filters available experts.
 * Essential first step in the inquiry form that enables progressive disclosure.
 * When expertCancerFocuses is provided, only shows cancer types the expert specializes in.
 *
 * Behavior:
 * - Loads cancer types from API with caching
 * - Filters to expert's focus areas when provided
 * - Displays cancer type labels only (no short labels)
 * - Validates required selection before form submission
 * - Triggers expert filtering when selection changes
 * - Shows `hint` under the field while it is empty
 *
 * Implementation:
 * - Uses testID prop on all Select components (converted to data-testid on web by React Native Web)
 * - Dynamic testIDs for SelectItem options: `cancer-type-option-${focusId}` for better debugging
 * - All testIDs use kebab-case naming convention for consistency with HTML/Playwright standards
 */
export function CancerTypeSelect({
  control,
  errors,
  isDisabled,
  expertCancerFocuses,
  onCancerTypeChange,
  hint,
}: CancerTypeSelectProps) {
  const { colors } = useColors();

  const insets = useSafeAreaInsets();

  const cancerFocusQuery = useQuery(getCancerTypesV2QueryOptions());

  const handleCancerTypeChange = (selectedCancerType: string) => {
    Keyboard.dismiss();

    onCancerTypeChange?.(selectedCancerType);
  };

  return (
    <Controller
      name="cancerFocusId"
      control={control}
      render={({ field: { onChange, value }, fieldState, formState }) => {
        const cancerType = cancerFocusQuery.data?.cancerTypeLookup.get(Number(value));

        const displayLabel = cancerType?.label || '';

        const showError =
          !!errors.cancerFocusId?.message && (fieldState.isTouched || formState.isSubmitted);

        return (
          <FormControl isInvalid={showError} isDisabled={isDisabled}>
            <FormControlLabel className="mb-2">
              <FormControlLabelText
                size="sm"
                className="font-semibold leading-normal text-typography-950">
                Cancer type*
              </FormControlLabelText>
            </FormControlLabel>

            <Select
              onValueChange={(selectedValue) => {
                onChange(selectedValue);

                handleCancerTypeChange(selectedValue);
              }}
              selectedValue={value}
              testID="cancer-type-select">
              <SelectTrigger className="flex w-full items-center justify-between p-2">
                <SelectInput
                  placeholder="Select cancer type"
                  style={{ color: colors.typography[900] }}
                  value={displayLabel}
                  testID="cancer-type-select-input"
                />
                <SelectIcon as={ChevronDown} />
              </SelectTrigger>

              <SelectPortal>
                <SelectBackdrop testID="cancer-type-select-backdrop" />
                <SelectContent
                  className="max-h-[70vh] w-full"
                  style={{ paddingBottom: insets.bottom }}
                  testID="cancer-type-select-content">
                  <SelectDragIndicatorWrapper>
                    <SelectDragIndicator />
                  </SelectDragIndicatorWrapper>

                  <SelectScrollView>
                    {cancerFocusQuery.data?.cancerTypes
                      ?.filter((cancerType) => {
                        if (expertCancerFocuses && expertCancerFocuses.length > 0) {
                          return expertCancerFocuses.some((focus) => focus.id === cancerType.id);
                        }

                        return true;
                      })
                      ?.map((cancerType) => {
                        const focusId = cancerType.id?.toString() ?? '';

                        const displayLabel = cancerType.label ?? '';

                        const testID = `cancer-type-option-${focusId}`;

                        return (
                          <SelectItem
                            key={focusId}
                            label={displayLabel}
                            value={focusId}
                            testID={testID}
                          />
                        );
                      })}
                  </SelectScrollView>
                </SelectContent>
              </SelectPortal>
            </Select>

            {hint && !value ? (
              <FormControlHelper className="mt-2">
                <FormControlHelperText
                  size="xs"
                  className="text-typography-700"
                  testID="cancer-type-select-hint">
                  Your conversation was about {hint}. Pick the closest match.
                </FormControlHelperText>
              </FormControlHelper>
            ) : null}

            <FormControlError className="mt-2">
              <FormControlErrorIcon as={AlertCircleIcon} />
              <FormControlErrorText size="xs">{errors.cancerFocusId?.message}</FormControlErrorText>
            </FormControlError>
          </FormControl>
        );
      }}
    />
  );
}
